import { setDetailsForReceiptPrint } from "./ReceiptHelper";
import EscPosEncoder from "./EscPosEncoder";

// Module-level reference to the connected Web Bluetooth device
let connectedBluetoothDevice = null;
let bluetoothCharacteristic = null;

// Session-based bill counter: starts at 1 when the system starts, increments with each printed bill
let __billCounter = 0;
export const getNextBillNumber = () => ++__billCounter;
export const getCurrentBillNumber = () => __billCounter;
export const resetBillCounter = () => { __billCounter = 0; };

export const setBluetoothDevice = (device, characteristic) => {
  connectedBluetoothDevice = device;
  bluetoothCharacteristic = characteristic;
};

export const getBluetoothDevice = () => {
  return connectedBluetoothDevice;
};

// Print via Web Bluetooth (ESC/POS)
export const printViaWebBluetooth = async (html, text) => {
  if (!bluetoothCharacteristic) {
    throw new Error("No Bluetooth printer connected");
  }

  try {
    const encoder = new EscPosEncoder();
    encoder.initialize();
    
    // Split the text into lines and print
    const cleanText = (text || '').replace(/<\/?b>|<\/?big>|<\/?center>/g, '');
    const lines = cleanText.split('\n');
    lines.forEach(line => {
      encoder.line(line);
    });
    
    encoder.cut();
    
    const buffer = encoder.encode();
    
    // Bluetooth characteristic value is limited in size (usually 512 bytes)
    // We need to chunk the data
    const CHUNK_SIZE = 300;
    for (let i = 0; i < buffer.length; i += CHUNK_SIZE) {
      const chunk = buffer.slice(i, i + CHUNK_SIZE);
      await bluetoothCharacteristic.writeValue(chunk);
    }
    return "Printed successfully via Bluetooth";
  } catch (error) {
    console.error("Bluetooth printing failed:", error);
    throw error;
  }
};

// Direct silent printing via local WebSocket agent
export const printViaWebSocketAgent = (printerName, html, text) => {
  return new Promise((resolve, reject) => {
    const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
    const ws = new WebSocket(wsUrl);

    // Timeout in case agent is unreachable
    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error("Connection timed out"));
    }, 2000);

    ws.onopen = () => {
      clearTimeout(timeout);
      ws.send(JSON.stringify({
        printerName,
        html,
        text
      }));
    };

    ws.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        if (response.type === 'print_response') {
          if (response.success) {
            resolve(response.message);
          } else {
            reject(new Error(response.message));
          }
        }
      } catch (err) {
        reject(err);
      } finally {
        ws.close();
      }
    };

    ws.onerror = (err) => {
      clearTimeout(timeout);
      reject(err);
    };
  });
};

// Fetch list of printers from the local WebSocket print agent
export const fetchPrintersFromWebSocketAgent = () => {
  return new Promise((resolve, reject) => {
    const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
    const ws = new WebSocket(wsUrl);

    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error("Connection timed out"));
    }, 2000);

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'get_printers' }));
    };

    ws.onmessage = (event) => {
      clearTimeout(timeout);
      try {
        const response = JSON.parse(event.data);
        if (response.type === 'get_printers_response') {
          resolve(response.printers || []);
        } else {
          reject(new Error("Invalid response type from agent"));
        }
      } catch (err) {
        reject(err);
      } finally {
        ws.close();
      }
    };

    ws.onerror = (err) => {
      clearTimeout(timeout);
      reject(err);
    };
  });
};

// Plain text preformatted KOT for raw thermal paper layout
export const generateKOTPlainText = (items, details) => {
  const { table_title, tokenNo, orderId } = details || {};
  const waiterName = details?.waiterName || details?.waiter_name || details?.waiter || details?.created_by || "";
  const isRoom = details?.deliveryType === 'room' || details?.delivery_type === 'room' || !!details?.room_name || !!details?.source || (table_title && String(table_title).toLowerCase().includes('room'));
  const roomName = details?.room_name || details?.source || (table_title ? String(table_title).replace(/^room\s*/i, '') : '');
  const diningLabel = isRoom
    ? `ROOM: ${roomName ? (String(roomName).toLowerCase().startsWith('room') ? String(roomName).toUpperCase() : `ROOM ${String(roomName).toUpperCase()}`) : 'ROOM ORDER'}`
    : (table_title ? `TABLE: ${String(table_title).toUpperCase()}` : (details?.deliveryType ? String(details.deliveryType).toUpperCase() : "TAKEAWAY / DELIVERY"));

  const separator = "--------------------------------\n";
  const doubleSeparator = "================================\n";
  
  let txt = "";
  txt += doubleSeparator;
  txt += "<center><b><big>*** KITCHEN ORDER (KOT) ***</big></b></center>\n";
  if (details?.copyLabel) {
    txt += `<center><b>[ ${details.copyLabel} ]</b></center>\n`;
  }
  txt += `<b>TOKEN: ${tokenNo || "-"} | ORDER #${orderId || ""}</b>\n`;
  txt += `<b><big>${diningLabel}</big></b>\n`;
  if (waiterName) {
    txt += `<b>WAITER: ${waiterName.toUpperCase()}</b>\n`;
  }
  txt += `DATE/TIME: ${new Date().toLocaleString()}\n`;
  txt += doubleSeparator;
  
  (items || []).forEach((item) => {
    const vTitle = item.variant_title || item.variant?.title || (typeof item.variant === 'string' ? item.variant : '') || '';
    const itemTitle = item.title || item.item_title || '';
    const qty = item.quantity ?? item.qty ?? 1;

    txt += `<b><big>[ ${qty}x ] ${itemTitle}</big></b>\n`;
    if (vTitle) {
      txt += `<b>   >> VARIANT: ${vTitle}</b>\n`;
    }
    if (item.addons && item.addons.length > 0) {
      const addonList = Array.isArray(item.addons)
        ? item.addons.map(a => typeof a === 'object' ? (a.title || a.name || '') : String(a)).filter(Boolean).join(", ")
        : String(item.addons);
      if (addonList) {
        txt += `<b>   + ADDONS: ${addonList}</b>\n`;
      }
    }
    if (item.notes) {
      txt += `<b>   *** NOTE: ${item.notes} ***</b>\n`;
    }
    txt += separator;
  });
  
  txt += "<center>--- END OF KOT ---</center>\n";
  txt += doubleSeparator;
  return txt;
};


function __groupReceiptItems(items) {
  if (!items || !Array.isArray(items)) return items;
  const grouped = [];
  items.forEach(item => {
    const addonsKey = Array.isArray(item.addons_ids) 
      ? item.addons_ids.slice().sort().join(',') 
      : (Array.isArray(item.addons) ? item.addons.map(a=>a.id || a).sort().join(',') : '');
    const priceKey = item.price || 0;
    const variantKey = item.variant?.title || item.variant_title || '';
    const key = (item.title || item.item_title || '') + '_' + variantKey + '_' + addonsKey + '_' + priceKey;
    
    const existing = grouped.find(g => g._groupKey === key);
    if (existing) {
      existing.quantity = Number(existing.quantity) + Number(item.quantity || 1);
    } else {
      grouped.push({ ...item, _groupKey: key, quantity: Number(item.quantity || 1) });
    }
  });
  return grouped;
}

export const generateReceiptPlainText = (details) => {
  details.cartItems = __groupReceiptItems(details.cartItems);
  const { 
    cartItems, deliveryType, customerType, customer, tableId, currency, storeSettings, printSettings,
    store_name, store_address, store_phone, store_email, tax_id,
    orderId, tokenNo, paymentMethod, itemsTotal, taxTotal, serviceChargeTotal, payableTotal, storedDiscount, roundOff, paidAmount, dueAmount
  } = details || {};

  const storeName = store_name || storeSettings?.store_name || "RESTAURANT";
  const storeAddress = store_address || storeSettings?.address || "";
  const storePhone = store_phone || storeSettings?.phone || "";
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');

  const header = printSettings?.header || "";
  const footer = printSettings?.footer || "";
  
  const rawCustomerName = customer?.name || details?.customerName || details?.customer_name || details?.guest_name || '';
  const rawCustomerId = customer?.id || details?.customerId || details?.customer_id || null;
  const rawCustomerType = customerType || details?.customer_type || '';

  const hasCustomer = Boolean(rawCustomerId && String(rawCustomerId).trim().toUpperCase() !== 'WALK-IN' && String(rawCustomerId).trim().toUpperCase() !== 'WALKIN');
  const isWalkinName = !rawCustomerName || ['walkin', 'walk-in', 'walk-in guest', 'walk-in customer', 'walk in', 'cash customer', 'guest'].includes(rawCustomerName.trim().toLowerCase());
  const isWalkinType = ['walkin', 'walk-in'].includes(String(rawCustomerType).trim().toLowerCase());

  const isWalkin = (!hasCustomer && isWalkinName) || isWalkinType;
  const displayCustomerType = isWalkin ? 'Walk-in' : (rawCustomerType && !['walkin', 'walk-in'].includes(String(rawCustomerType).trim().toLowerCase()) ? (String(rawCustomerType).trim().toLowerCase() === 'room' ? 'Room Guest' : (String(rawCustomerType).trim().toUpperCase() === 'CUSTOMER' ? 'Customer' : rawCustomerType)) : 'Customer');
  const displayCustomerName = isWalkin ? 'Walk-in' : (rawCustomerName || 'Walk-in');

  let txt = "";
  
  // Header
  if (storeName) txt += `      ${storeName}      \n`;
  if (storeAddress) txt += `  ${storeAddress}  \n`;
  if (storePhone) txt += `  Tel: ${storePhone}  \n`;
  if (header) txt += `\n${header}\n`;
  txt += "--------------------------------\n";

  if (orderId) txt += `Order #${orderId}\n`;
  if (details.billNo) txt += `Bill No: ${details.billNo}\n`;
  if (tokenNo) txt += `Token: ${tokenNo}\n`;
  
  const rawRoom = details.room_name || details.room || details.source || '';
  const rawTableTitle = (details.table_title || details.tableName || '').trim();
  const isRoomOrder = deliveryType === 'room' || 
                      Boolean(rawRoom) || 
                      (rawTableTitle && rawTableTitle.toLowerCase().startsWith('room'));

  const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();
  if (cleanTitle) {
    txt += `Room/Table: ${cleanTitle}\n`;
  }
  
  txt += `Type: ${displayCustomerType}\n`;
  txt += `Customer: ${displayCustomerName}\n`;
  if (customer?.pan_no) txt += `Buyer PAN: ${customer.pan_no}\n`;
  if (customer?.phone) txt += `Phone: ${customer.phone}\n`;

  if (details.orderTime) {
    txt += `Ordered: ${new Date(details.orderTime).toLocaleString()}\n`;
  }
  const printDateStr = details.billedTime ? new Date(details.billedTime).toLocaleString() : ((details.date || details.created_at) ? new Date(details.date || details.created_at).toLocaleString() : new Date().toLocaleString());
  txt += `Billed: ${printDateStr}\n`;
  txt += "--------------------------------\n";

  // Items
  cartItems?.forEach(item => {
    const qty = String(item.quantity || item.qty || 1).padEnd(3);
    const itemTitle = item.title || item.item_title || '';
    const variantTitle = item.variant_title || item.variant?.title || '';
    const title = (itemTitle + (variantTitle ? ` - ${variantTitle}` : '')).substring(0, 18).padEnd(19);
    const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
    const pBase = safeNum(item.price || item.rate);
    const pVar = safeNum(item.variant_price || item.variant?.price);
    const priceVal = pBase * safeNum(item.quantity || item.qty || 1);
    const price = (curSymbol + " " + priceVal.toFixed(2)).padStart(10);
    
    txt += `${qty}${title}${price}\n`;
    
    if (item.addons && item.addons.length > 0) {
      txt += `   + Addons\n`;
    }
  });
  
  txt += "--------------------------------\n";
  txt += `Subtotal:       ${String(curSymbol + " " + Number(itemsTotal || 0).toFixed(2)).padStart(16)}\n`;
  if (Number(storedDiscount || 0) > 0) txt += `Discount:       -${String(curSymbol + " " + Number(storedDiscount).toFixed(2)).padStart(15)}\n`;
  if (serviceChargeTotal > 0) txt += `Service Charge: +${String(curSymbol + " " + Number(serviceChargeTotal).toFixed(2)).padStart(15)}\n`;
  
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
  if (isVat || taxTotal > 0) {
      const taxable = details.taxableAmount !== undefined ? details.taxableAmount : Math.max(0, Number(itemsTotal || 0) - Number(storedDiscount || 0) + Number(serviceChargeTotal || 0));
      txt += `Taxable Amount: ${String(curSymbol + " " + Number(taxable).toFixed(2)).padStart(16)}\n`;
  }

  if (taxTotal > 0) txt += `${isVat ? '13% VAT' : 'Tax'}:          +${String(curSymbol + " " + Number(taxTotal).toFixed(2)).padStart(15)}\n`;
  if (Number(roundOff || 0) !== 0) txt += `Round Off:      ${String((Number(roundOff) > 0 ? '+' : '') + curSymbol + " " + Number(roundOff).toFixed(2)).padStart(16)}\n`;
  txt += `TOTAL:          ${String(curSymbol + " " + Number(payableTotal || 0).toFixed(2)).padStart(16)}\n`;
  
  if (paymentMethod) txt += `Payment Mode:   ${paymentMethod.padStart(16)}\n`;
  if (Number(paidAmount || 0) > 0 && Number(paidAmount || 0) !== Number(payableTotal || 0)) {
      txt += `Received:       ${String(curSymbol + " " + Number(paidAmount).toFixed(2)).padStart(16)}\n`;
  }
  if (Number(dueAmount || 0) > 0) {
      txt += `Due Balance:    ${String(curSymbol + " " + Number(dueAmount).toFixed(2)).padStart(16)}\n`;
  }
  const receiptNote = details.notes || details.note || "";
  if (receiptNote) {
      txt += `Note: ${receiptNote}\n`;
  }
  txt += "--------------------------------\n";
  
  if (footer) txt += `\n${footer}\n`;
  txt += `       Thank You!       \n\n\n`;
  
  return txt;
};

export const printReceiptViaWebBluetooth = async (details) => {
  const plainText = generateReceiptPlainText(details);
  await printViaWebBluetooth(null, plainText);
};

export const getElectron = () => {
  if (typeof window === 'undefined') return null;
  if (window.electron) return window.electron;
  if (typeof window.require === 'function') {
    try {
      const el = window.require('electron');
      if (el) return el;
    } catch (e) {}
  }
  return null;
};

export const isElectronEnv = () => {
  return typeof window !== 'undefined' && Boolean(
    window.isElectron ||
    window.electron ||
    (window.require && typeof window.require === 'function') ||
    (window.process && window.process.type) ||
    (navigator.userAgent && navigator.userAgent.toLowerCase().includes('electron'))
  );
};

export const groupItemsByPrinter = (cartItems, categories = []) => {
  const groups = {};
  const defaultPrinter = localStorage.getItem("billPrinter") || "Default";

  // Fallback to cached categories if none provided
  if (!categories || categories.length === 0) {
    try {
      const cached = localStorage.getItem("cached_categories");
      if (cached) {
        categories = JSON.parse(cached);
      } else {
        const bootstrap = localStorage.getItem("restro_pos_bootstrap_v1");
        if (bootstrap) {
          const parsed = JSON.parse(bootstrap);
          if (parsed && Array.isArray(parsed.categories)) {
            categories = parsed.categories;
          }
        }
      }
    } catch(e) {}
  }
  
  let categoryCopiesMap = {};
  try {
    categoryCopiesMap = JSON.parse(localStorage.getItem("category_kot_copies") || "{}");
  } catch (e) {}

  let categoryPrintersMap = {};
  try {
    categoryPrintersMap = JSON.parse(localStorage.getItem("category_printers_map") || "{}");
  } catch (e) {}

  (cartItems || []).forEach((item) => {
    let printerName = null;
    let copies = 1;
    const categoryId = item.category_id != null ? item.category_id : (item.category != null ? item.category : item.categoryId);
    const categoryTitle = item.category_title || item.categoryTitle || (typeof item.category === 'string' && isNaN(Number(item.category)) ? item.category : null);

    // 1. Identify category from ID or Title
    let matchedCategory = null;
    if (Array.isArray(categories) && categories.length > 0) {
      matchedCategory = categories.find((c) => 
        c && (
          (categoryId != null && String(c.id) === String(categoryId)) ||
          (categoryTitle && c.title && c.title.toLowerCase() === categoryTitle.toLowerCase()) ||
          (categoryId && typeof categoryId === 'string' && c.title && c.title.toLowerCase() === categoryId.toLowerCase())
        )
      );
    }

    // 2. Resolve printer name and copies from maps / localStorage keys
    const catKeysToCheck = [
      categoryId,
      matchedCategory ? matchedCategory.id : null,
      categoryTitle,
      matchedCategory ? matchedCategory.title : null
    ].filter(k => k != null && k !== '').map(String);

    for (const key of catKeysToCheck) {
      if (!printerName) {
        const p = categoryPrintersMap[key] || localStorage.getItem(`category_printer_${key}`);
        if (p && p.trim() !== '') printerName = p.trim();
      }
      const c = categoryCopiesMap[key] || localStorage.getItem(`category_copies_${key}`);
      if (c) {
        const parsed = parseInt(c);
        if (parsed > copies) copies = parsed;
      }
    }

    // 2.5 Check matched category object printer
    if (!printerName && matchedCategory && matchedCategory.printer && matchedCategory.printer.trim() !== '') {
      printerName = matchedCategory.printer.trim();
    }

    // 2.6 Check item.category_printer (from DB category join)
    if (!printerName && item.category_printer && typeof item.category_printer === 'string' && item.category_printer.trim() !== '') {
      printerName = item.category_printer.trim();
    }

    // 3. Fallback to item-level printer if configured
    if (!printerName && item.printer && typeof item.printer === 'string' && item.printer.trim() !== '') {
      printerName = item.printer.trim();
    }

    // 4. Fallback to default bill printer
    if (!printerName || printerName === 'Default') {
      printerName = defaultPrinter || "Default";
    }

    // 5. Check if printer itself has specific copies configured
    if (copies === 1 && printerName) {
      const printerCopies = parseInt(localStorage.getItem(`printer_copies_${printerName}`));
      if (printerCopies && printerCopies > 1) {
        copies = printerCopies;
      }
    }

    // 6. Check default KOT copies or global print count
    if (copies === 1) {
      const defaultKotCopies = parseInt(localStorage.getItem('kot_copies')) || 
                               parseInt(localStorage.getItem('category_default_copies')) || 
                               parseInt(localStorage.getItem('printCount'));
      if (defaultKotCopies && defaultKotCopies > 1) {
        copies = defaultKotCopies;
      }
    }

    if (!groups[printerName]) {
      groups[printerName] = {
        printerName: printerName,
        copies: copies,
        items: []
      };
    } else {
      // Use highest copy count among items in this group
      if (copies > groups[printerName].copies) {
        groups[printerName].copies = copies;
      }
    }
    groups[printerName].items.push(item);
  });

  return groups;
};

export const printKOT = async (printerGroups, orderDetails = {}) => {
  const electron = getElectron();
  const isElectron = isElectronEnv();
  const isBluetooth = !!connectedBluetoothDevice;
  const pageFormat = orderDetails.pageFormat || orderDetails.page_format || localStorage.getItem('page_format') || '58';

  for (const [printerKey, groupData] of Object.entries(printerGroups)) {
    // Support both old format (array of items) and new format ({ printerName, copies, items })
    const items = Array.isArray(groupData) ? groupData : (groupData.items || []);
    if (!items || items.length === 0) continue;

    const printerName = (groupData && groupData.printerName) ? groupData.printerName : printerKey;
    const groupCopies = (groupData && groupData.copies) ? parseInt(groupData.copies) : (parseInt(localStorage.getItem(`printer_copies_${printerName}`)) || parseInt(localStorage.getItem('printCount')) || 1);
    const totalCopies = Math.max(1, groupCopies);

    console.log(`[PrinterHelper] Printing KOT to "${printerName}" (${items.length} items, Copies: ${totalCopies}, Format: ${pageFormat}):`, items);

    // Print each copy with its own label: COPY 1 OF 2, COPY 2 OF 2... (matching Flutter KDS)
    for (let c = 0; c < totalCopies; c++) {
      const copyLabel = totalCopies > 1 ? `COPY ${c + 1} OF ${totalCopies}` : null;
      const copyOrderDetails = {
        ...orderDetails,
        copyLabel: copyLabel,
        copy_label: copyLabel,
        copyNumber: c + 1,
        totalCopies: totalCopies
      };

      const html = generateKOTHTML(items, copyOrderDetails, pageFormat);
      const plainText = generateKOTPlainText(items, copyOrderDetails, pageFormat);

      if (isBluetooth) {
        try {
          console.log(`[Web POS] Attempting direct Web Bluetooth printing (${c + 1}/${totalCopies})...`);
          await printViaWebBluetooth(html, plainText);
          console.log(`[Web POS] Web Bluetooth print succeeded.`);
          continue;
        } catch (btErr) {
          console.warn(`[Web POS] Web Bluetooth print failed:`, btErr);
        }
      }

      if (isElectron) {
        try {
          const defaultPrinter = localStorage.getItem("billPrinter") || "";
          const targetPrinter = (printerName && printerName !== "Default") ? printerName : defaultPrinter;

          const payload = {
            printerName: targetPrinter,
            html: html,
            plainText: plainText,
            copies: 1, // each copy sent individually with copyLabel
            pageFormat: pageFormat
          };

          if (electron && electron.ipcRenderer) {
            electron.ipcRenderer.send('print-kot', payload);
          } else if (electron && typeof electron.printKOT === 'function') {
            electron.printKOT(payload);
          } else if (window.require) {
            window.require('electron').ipcRenderer.send('print-kot', payload);
          }
          continue;
        } catch (electronErr) {
          console.warn("Electron silent KOT print failed, falling back to agent:", electronErr);
        }
      }

      // WEB BROWSER: Try WebSocket Print Agent first, fallback to browser print dialog
      try {
        console.log(`[Web POS] Attempting silent WebSocket printing to "${printerName}" (${c + 1}/${totalCopies})...`);
        await printViaWebSocketAgent(printerName, html, plainText);
        console.log(`[Web POS] Silent print succeeded for "${printerName}"`);
      } catch (wsErr) {
        console.warn(`[Web POS] WebSocket Print Agent failed or unreachable:`, wsErr.message);
        console.log(`[Web POS] Falling back to standard browser print dialog...`);

        setDetailsForReceiptPrint({
          ...copyOrderDetails,
          cartItems: items
        });

        const receiptWindow = window.open("/print-kot", "_blank", "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=400,height=400");
        if (receiptWindow) {
          receiptWindow.onload = () => {
            setTimeout(() => {
              receiptWindow.print();
            }, 800);
          };
        }
      }
    }
  }
};

export const generateKOTHTML = (items, details, pageFormat = '80') => {
  const { tableId, table_title, tokenNo, orderId, customerName, copyLabel } = details || {};
  const waiterName = details.waiterName || details.waiter_name || details.waiter || details.created_by || "";
  
  const isRoom = details.deliveryType === 'room' || details.delivery_type === 'room' || !!details.room_name || !!details.source || (table_title && table_title.toLowerCase().includes('room'));
  const roomName = details.room_name || details.source || (table_title ? table_title.replace(/^room\s*/i, '') : '');
  const diningLabel = isRoom 
    ? `ROOM: ${roomName ? (roomName.toLowerCase().startsWith('room') ? roomName.toUpperCase() : roomName.toUpperCase()) : 'ROOM ORDER'}` 
    : (table_title ? `TABLE: ${table_title.toUpperCase()}` : (details.deliveryType ? details.deliveryType.toUpperCase() : "TAKEAWAY / DELIVERY"));

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const paperWidthCss = is58mm ? '48mm' : '72mm';
  const bodyPadding = is58mm ? '1mm' : '2mm 1mm';
  const titleSize = is58mm ? '1.25rem' : '1.55rem';
  const diningBoxSize = is58mm ? '1.2rem' : '1.45rem';
  const itemTitleSize = is58mm ? '1.15rem' : '1.35rem';
  const qtyBoxSize = is58mm ? '1.3rem' : '1.6rem';
  const waiterBoxSize = is58mm ? '1.05rem' : '1.2rem';

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          @page { 
            size: auto; 
            margin: 0 !important; 
          }
          * { 
            box-sizing: border-box; 
          }
          html, body { 
            font-family: 'Courier New', Courier, monospace; 
            width: 100% !important;
            margin: 0 !important; 
            padding: 0 !important; 
            color: #000; 
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt-wrapper {
            width: 100% !important;
            max-width: ${paperWidthCss} !important;
            margin: 0 auto !important;
            padding: ${bodyPadding} !important;
            box-sizing: border-box !important;
          }
          .header { text-align: center; border-bottom: 2.5px solid #000; padding-bottom: 5px; margin-bottom: 6px; }
          .title { font-size: ${titleSize}; font-weight: 900; letter-spacing: 0.5px; }
          .copy-banner { border: 2px solid #000; padding: 2px 4px; margin: 3px 0; font-weight: 900; font-size: 1rem; text-align: center; }
          .token-table { font-size: 1.15rem; font-weight: 900; margin: 4px 0; }
          .dining-box { border: 2.5px solid #000; padding: 4px 6px; margin: 5px 0; font-weight: 900; font-size: ${diningBoxSize}; text-align: center; background: #fff; letter-spacing: 0.5px; }
          .waiter-box { border: 2px solid #000; padding: 3px 5px; margin: 4px 0; font-weight: 900; font-size: ${waiterBoxSize}; text-align: center; }
          .item-container { margin-bottom: 6px; border-bottom: 1.5px dashed #000; padding-bottom: 5px; }
          .item { display: flex; align-items: flex-start; gap: 8px; }
          .item-qty-box { font-size: ${qtyBoxSize}; font-weight: 900; border: 2.5px solid #000; padding: 2px 6px; border-radius: 4px; min-width: 32px; text-align: center; line-height: 1.1; }
          .item-content { flex: 1; min-width: 0; }
          .item-title { font-weight: 900; font-size: ${itemTitleSize}; line-height: 1.15; word-break: break-word; }
          .variant-title { font-weight: 900; font-size: 1.05rem; margin-top: 2px; }
          .addons { font-size: 0.95rem; font-weight: bold; margin-top: 3px; }
          .notes { font-size: 0.95rem; font-weight: 900; margin-top: 3px; background: #f0f0f0; padding: 2px 4px; border-left: 3px solid #000; }
          .footer { border-top: 2.5px solid #000; margin-top: 8px; padding-top: 5px; text-align: center; font-size: 0.95rem; font-weight: 900; }
        </style>
      </head>
      <body>
        <div class="receipt-wrapper">
          <div class="header">
            <div class="title">*** KITCHEN ORDER (KOT) ***</div>
            ${copyLabel ? `<div class="copy-banner">*** ${copyLabel} ***</div>` : ''}
            <div class="token-table">TOKEN: ${tokenNo || "-"} | ORDER #${orderId || ""}</div>
            <div class="dining-box">${diningLabel}</div>
            ${waiterName ? `<div class="waiter-box">WAITER: ${waiterName.toUpperCase()}</div>` : ""}
            <div style="font-size: 0.85rem; font-weight: bold; color: #000; margin-top: 3px;">DATE/TIME: ${new Date().toLocaleString()}</div>
          </div>
          <div class="items">
            ${items.map(item => {
              const vTitle = item.variant_title || item.variant?.title || (typeof item.variant === 'string' ? item.variant : '') || '';
              const itemTitle = item.title || item.item_title || '';
              const qty = item.quantity ?? item.qty ?? 1;
              return `
              <div class="item-container">
                <div class="item">
                  <span class="item-qty-box">${qty}</span>
                  <div class="item-content">
                    <div class="item-title">${itemTitle}</div>
                    ${vTitle ? `<div class="variant-title">&gt;&gt; ${vTitle}</div>` : ''}
                    ${item.addons?.length > 0 ? `<div class="addons">+ ADDONS: ${item.addons.map(a => typeof a === 'object' ? (a.title || a.name || '') : String(a)).filter(Boolean).join(", ")}</div>` : ""}
                    ${item.notes ? `<div class="notes">*** NOTE: ${item.notes} ***</div>` : ""}
                  </div>
                </div>
              </div>
              `;
            }).join("")}
          </div>
          <div class="footer">
            --- END OF KOT ---
          </div>
          <!-- Clearance feed so thermal paper cutter doesn't cut through text -->
          <div style="height: 10mm; clear: both;"></div>
        </div>
      </body>
    </html>
  `;
};

export const generateReceiptHTML = (details, pageFormat = '58') => {
  details.cartItems = __groupReceiptItems(details.cartItems);
  const { 
    cartItems = [], deliveryType = '', customerType = '', customer = null, 
    currency = 'Rs.', storeSettings = {}, printSettings = {},
    itemsTotal = 0, taxTotal = 0, serviceChargeTotal = 0, payableTotal = 0,
    storedDiscount = 0, roundOff = 0, paidAmount = undefined, dueAmount = 0,
    tokenNo = '', orderId = '', paymentMethod = '',
    table_title = '', isEstimate = false
  } = details || {};

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const paperWidthCss = is58mm ? '48mm' : '72mm';
  const bodyPadding = is58mm ? '1mm' : '2mm 1mm';
  const titleSize = is58mm ? '1.1rem' : '1.35rem';
  const textSize = is58mm ? '0.8rem' : '0.9rem';
  const tableSize = is58mm ? '0.75rem' : '0.85rem';

  const storeName = storeSettings?.store_name || details?.store_name || 'RESTAURANT';
  const address = storeSettings?.address || details?.address || '';
  const phone = storeSettings?.phone || details?.phone || '';
  const panNo = storeSettings?.pan_no || storeSettings?.panNo || details?.pan_no || '';
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
  const headerText = printSettings?.header || details?.header || '';
  const footerText = printSettings?.footer || details?.footer || 'Thank You! Visit Again';
  
  const rawCustomerName = customer?.name || details?.customerName || details?.customer_name || details?.guest_name || '';
  const rawCustomerId = customer?.id || details?.customerId || details?.customer_id || null;
  const rawCustomerType = customerType || details?.customer_type || '';

  const hasCustomer = Boolean(rawCustomerId && String(rawCustomerId).trim().toUpperCase() !== 'WALK-IN' && String(rawCustomerId).trim().toUpperCase() !== 'WALKIN');
  const isWalkinName = !rawCustomerName || ['walkin', 'walk-in', 'walk-in guest', 'walk-in customer', 'walk in', 'cash customer', 'guest'].includes(rawCustomerName.trim().toLowerCase());
  const isWalkinType = ['walkin', 'walk-in'].includes(String(rawCustomerType).trim().toLowerCase());

  const isWalkin = (!hasCustomer && isWalkinName) || isWalkinType;
  const displayCustomerType = isWalkin ? 'Walk-in' : (rawCustomerType && !['walkin', 'walk-in'].includes(String(rawCustomerType).trim().toLowerCase()) ? (String(rawCustomerType).trim().toLowerCase() === 'room' ? 'Room Guest' : (String(rawCustomerType).trim().toUpperCase() === 'CUSTOMER' ? 'Customer' : rawCustomerType)) : 'Customer');
  const displayCustomerName = isWalkin ? 'Walk-in' : (rawCustomerName || 'Walk-in');

  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');
  const fmt = (v) => `${curSymbol} ${Number(v || 0).toFixed(2)}`;

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${isEstimate ? 'Estimate Bill (कच्चा बिल)' : 'Receipt'}</title>
        <style>
          @page { 
            size: auto; 
            margin: 2mm 3mm !important; 
          }
          * { 
            box-sizing: border-box; 
          }
          html, body { 
            font-family: 'Courier New', Courier, monospace; 
            width: 100% !important;
            margin: 0 !important; 
            padding: 0 !important; 
            color: #000 !important; 
            background: #fff !important; 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt-wrapper {
            width: 100% !important;
            max-width: ${paperWidthCss} !important;
            margin: 0 auto !important;
            padding: ${is58mm ? '2mm 3mm' : '3mm 4mm'} !important;
            box-sizing: border-box !important;
            background: #fff !important;
            color: #000 !important;
          }
          .center { text-align: center; }
          .bold { font-weight: 900; }
          .store-name { font-size: ${titleSize}; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; }
          .sub-text { font-size: ${textSize}; line-height: 1.2; }
          .divider { border-bottom: 1px dashed #000; margin: 4px 0; }
          .double-divider { border-bottom: 2px solid #000; margin: 5px 0; }
          .banner { border: 2px solid #000; padding: 2px 4px; margin: 4px 0; font-weight: 900; text-align: center; }
          table.items { width: 100%; border-collapse: collapse; font-size: ${tableSize}; }
          table.items th { border-bottom: 1px solid #000; padding: 2px 0; text-align: left; }
          table.items td { padding: 2px 0; vertical-align: top; }
          .text-right { text-align: right; }
          .summary-row { display: flex; justify-content: space-between; font-size: ${textSize}; padding: 1px 0; }
          .grand-total { display: flex; justify-content: space-between; font-size: 1.1rem; font-weight: 900; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; padding: 3px 0; margin: 4px 0; }
          .cutter-space { height: 8mm; clear: both; }

          @media print {
            .no-print { display: none !important; }
            html, body { background: #fff !important; }
          }
          .action-bar {
            text-align: center;
            padding: 10px;
            background: #f1f5f9;
            border-bottom: 1px solid #cbd5e1;
            margin-bottom: 10px;
          }
          .btn-print {
            background: #16a34a;
            color: #fff;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: bold;
            border-radius: 6px;
            border: none;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .btn-close {
            background: #e2e8f0;
            color: #334155;
            padding: 8px 12px;
            font-size: 13px;
            font-weight: bold;
            border-radius: 6px;
            border: none;
            cursor: pointer;
            margin-left: 8px;
          }
        </style>
      </head>
      <body>
        <div class="no-print action-bar">
          <button class="btn-print" onclick="window.print()">🖨️ ${isEstimate ? 'Print Estimate Bill (कच्चा बिल)' : 'Print Tax Invoice'}</button>
          <button class="btn-close" onclick="window.close()">✕ Close</button>
        </div>
        <div class="receipt-wrapper">
          <div class="center">
            <div class="store-name">${storeName}</div>
            ${address ? `<div class="sub-text">${address}</div>` : ''}
            ${phone ? `<div class="sub-text">Tel: ${phone}</div>` : ''}
            ${panNo ? `<div class="sub-text bold">${isVat ? 'VAT' : 'PAN'}: ${panNo}</div>` : ''}
            ${headerText ? `<div class="sub-text" style="margin-top:2px;">${headerText}</div>` : ''}
          </div>

          ${isEstimate ? `
            <div class="banner">
              <div style="font-size: 0.95rem;">PROFORMA ESTIMATE</div>
              <div style="font-size: 0.72rem;">(अनुमानित बिल - NOT A TAX INVOICE)</div>
            </div>
          ` : `
            <div class="banner">
              <div style="font-size: 0.95rem;">${isVat ? 'TAX INVOICE' : 'INVOICE'}</div>
              <div style="font-size: 0.72rem;">(कर बिजक)</div>
            </div>
          `}

          <div class="sub-text" style="margin-top: 4px;">
            ${orderId ? `<div><b>Order #:</b> ${orderId}</div>` : ''}
            ${details.billNo ? `<div><b>Bill No:</b> ${details.billNo}</div>` : ''}
            ${tokenNo ? `<div><b>Token #:</b> ${tokenNo}</div>` : ''}
            ${(() => {
              const rawRoom = details.room_name || details.room || details.source || '';
              const rawTableTitle = (table_title || details.table_title || details.tableName || '').trim();
              const isRoomOrder = deliveryType === 'room' || Boolean(rawRoom) || (rawTableTitle && rawTableTitle.toLowerCase().startsWith('room'));
              const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();
              if (cleanTitle) {
                return `<div><b>Room/Table:</b> ${cleanTitle}</div>`;
              }
              return '';
            })()}
            ${details?.waiterName ? `<div><b>Server:</b> ${details.waiterName}</div>` : ''}
            <div><b>Customer Type:</b> ${displayCustomerType}</div>
            <div><b>Customer:</b> ${displayCustomerName}</div>
            ${customer?.pan_no ? `<div><b>Buyer PAN:</b> ${customer.pan_no}</div>` : ''}
            ${customer?.phone ? `<div><b>Phone:</b> ${customer.phone}</div>` : ''}
            ${details.orderTime ? `<div><b>Ordered:</b> ${new Date(details.orderTime).toLocaleString()}</div>` : ''}
            <div><b>Billed:</b> ${details.billedTime ? new Date(details.billedTime).toLocaleString() : ((details.date || details.created_at) ? new Date(details.date || details.created_at).toLocaleString() : new Date().toLocaleString())}</div>
          </div>

          <div class="divider"></div>

          <table class="items">
            <thead>
              <tr>
                <th style="width: 15%;">Qty</th>
                <th style="width: 55%;">Item</th>
                <th style="width: 30%; text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${cartItems.map(item => {
                const qty = item.quantity || item.qty || 1;
                const title = item.title || item.item_title || '';
                const variant = item.variant_title || item.variant?.title || '';
                const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
                const pBase = safeNum(item.price || item.rate);
                const pVar = safeNum(item.variant_price || item.variant?.price);
                // If they are equal, it means oi.price was coalesced into both, so don't add.
                // Generally, if variant price is present, we shouldn't add base price.
                // Wait, if pBase already includes pVar, we just use pBase.
                const price = pBase;
                const lineTotal = fmt(price * qty);
                return `
                  <tr>
                    <td>${qty}</td>
                    <td>
                      <b>${title}</b> ${variant ? `(${variant})` : ''}
                      ${item.addons?.length > 0 ? `<div style="font-size: 0.7rem; color: #333;">+ ${item.addons.map(a => a.title).join(', ')}</div>` : ''}
                      ${item.notes ? `<div style="font-size: 0.7rem; font-style: italic;">* ${item.notes}</div>` : ''}
                    </td>
                    <td class="text-right">${lineTotal}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div class="divider"></div>

          <div class="summary-row"><span>Subtotal:</span><span>${fmt(itemsTotal)}</span></div>
          ${Number(storedDiscount || 0) > 0 ? `<div class="summary-row"><span>Discount:</span><span>-${fmt(storedDiscount)}</span></div>` : ''}
          ${Number(serviceChargeTotal || 0) > 0 ? `<div class="summary-row"><span>Service Chg:</span><span>+${fmt(serviceChargeTotal)}</span></div>` : ''}
          
          ${(isVat || Number(taxTotal || 0) > 0) ? `<div class="summary-row"><span>Taxable Amount:</span><span>${fmt(details.taxableAmount !== undefined ? details.taxableAmount : Math.max(0, Number(itemsTotal || 0) - Number(storedDiscount || 0) + Number(serviceChargeTotal || 0)))}</span></div>` : ''}
          
          ${Number(taxTotal || 0) > 0 ? `<div class="summary-row"><span>${isVat ? '13% VAT' : 'Tax'}:</span><span>+${fmt(taxTotal)}</span></div>` : ''}
          ${Number(roundOff || 0) !== 0 ? `<div class="summary-row"><span>Round Off:</span><span>${Number(roundOff) > 0 ? '+' : ''}${fmt(roundOff)}</span></div>` : ''}

          <div class="grand-total">
            <span>${isEstimate ? 'EST. TOTAL:' : 'NET TOTAL:'}</span>
            <span>${fmt(payableTotal)}</span>
          </div>

          ${!isEstimate && paymentMethod ? `<div class="summary-row bold"><span>Payment Mode:</span><span>${paymentMethod}</span></div>` : ''}
          ${!isEstimate && Number(details.advancePayment || 0) > 0 ? `<div class="summary-row"><span>Advance Paid:</span><span>-${fmt(details.advancePayment)}</span></div>` : ''}
          ${!isEstimate && Number(details.amountPaidNow || paidAmount || 0) > 0 ? `<div class="summary-row"><span>Paid at Checkout:</span><span>-${fmt(details.amountPaidNow || paidAmount)}</span></div>` : ''}
          ${!isEstimate && Number(paidAmount || 0) > 0 && !details.amountPaidNow ? `<div class="summary-row bold"><span>Total Paid:</span><span>${fmt(paidAmount)}</span></div>` : ''}
          ${!isEstimate && Number(dueAmount || 0) > 0 ? `<div class="summary-row bold" style="color:#d9534f;"><span>Due Balance:</span><span>${fmt(dueAmount)}</span></div>` : ''}
          ${(details.notes || details.note) ? `<div style="margin-top: 4px; padding: 2px 4px; font-size: 0.72rem; border-left: 2px solid #000; font-style: italic; word-break: break-word;"><b>Note:</b> ${details.notes || details.note}</div>` : ''}

          <div class="double-divider"></div>

          <div class="center sub-text" style="margin-top: 4px;">
            <div>${footerText}</div>
            <div style="font-size: 0.7rem; margin-top: 2px;">RestroPro POS • eHospitality</div>
          </div>

          <!-- Clearance feed so paper cutter doesn't cut through text -->
          <div class="cutter-space"></div>
        </div>
        <script>
          window.addEventListener('load', function() {
            setTimeout(function() {
              try { window.print(); } catch (e) {}
            }, 300);
          });
        </script>
      </body>
    </html>
  `;
};

export const printReceipt = async (details) => {
  const electron = getElectron();
  const isElectron = isElectronEnv();
  const isBluetooth = !!connectedBluetoothDevice;
  const billPrinter = localStorage.getItem("billPrinter") || "Default";
  const pageFormat = details.pageFormat || details.page_format || details.printSettings?.page_format || localStorage.getItem('page_format') || '58';
  const copies = details.copies || details.printSettings?.print_count || 1;

  // Use passed invoice number / bill number if available to match Invoices page, otherwise fallback to session counter
  details.billNo = details.billNo || details.invoice_id || details.invoiceId || details.invoiceNo || details.invoice_no || getNextBillNumber();

  const html = generateReceiptHTML(details, pageFormat);
  const plainText = generateReceiptPlainText(details);

  if (isBluetooth) {
    try {
      for (let c = 0; c < copies; c++) {
        await printViaWebBluetooth(html, plainText);
      }
      return;
    } catch (btErr) {
      console.warn("Bluetooth receipt print failed:", btErr);
    }
  }

  if (isElectron) {
    try {
      if (electron && electron.ipcRenderer) {
        electron.ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html: html,
          plainText: plainText,
          copies: copies,
          pageFormat: pageFormat
        });
        return;
      } else if (window.require) {
        window.require('electron').ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html: html,
          plainText: plainText,
          copies: copies,
          pageFormat: pageFormat
        });
        return;
      }
    } catch (elErr) {
      console.warn("Electron silent receipt print failed, falling back to agent:", elErr);
    }
  }

  // Web Browser fallback
  try {
    for (let c = 0; c < copies; c++) {
      await printViaWebSocketAgent(billPrinter, html, plainText);
    }
  } catch (wsErr) {
    console.warn("WebSocket agent unavailable, using direct offline HTML print window:", wsErr);
    setDetailsForReceiptPrint(details);

    let printed = false;
    try {
      const printWin = window.open("", "_blank", "toolbar=no,scrollbars=yes,resizable=yes,top=80,left=80,width=480,height=720");
      if (printWin) {
        printWin.document.open();
        printWin.document.write(html);
        printWin.document.close();
        printWin.focus();
        setTimeout(() => {
          try {
            printWin.print();
          } catch (e) {
            console.warn("Popup print trigger error:", e);
          }
        }, 300);
        printed = true;
      }
    } catch (popupErr) {
      console.warn("Popup open blocked or failed, using iframe fallback:", popupErr);
    }

    if (!printed) {
      let iframe = document.getElementById('receipt-print-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'receipt-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
      }
      try {
        iframe.contentDocument.open();
        iframe.contentDocument.write(html);
        iframe.contentDocument.close();
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.warn("Iframe print trigger error:", e);
          }
        }, 300);
      } catch (ifErr) {
        console.error("Iframe write failed:", ifErr);
      }
    }
  }
};

// Network Printer Local Storage helpers
export const getNetworkPrinters = () => {
  try {
    const raw = localStorage.getItem('network_printers');
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const saveNetworkPrinter = (printer) => {
  try {
    const list = getNetworkPrinters();
    const existingIndex = list.findIndex(p => p.id === printer.id || (p.ip === printer.ip && p.port === printer.port));
    if (existingIndex >= 0) {
      list[existingIndex] = { ...list[existingIndex], ...printer };
    } else {
      list.push(printer);
    }
    localStorage.setItem('network_printers', JSON.stringify(list));
    return list;
  } catch (e) {
    console.error('Failed to save network printer:', e);
    return [];
  }
};

export const deleteNetworkPrinter = (printerId) => {
  try {
    const list = getNetworkPrinters();
    const updated = list.filter(p => p.id !== printerId);
    localStorage.setItem('network_printers', JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error('Failed to delete network printer:', e);
    return [];
  }
};

// Universal Test Print function
export const testPrinter = async (printerInfo) => {
  const isElectron = isElectronEnv();
  const electron = getElectron();
  const { name, isIp, ip, port, format = '80' } = printerInfo;

  if (isElectron) {
    try {
      let result;
      if (electron && electron.testPrint) {
        result = await electron.testPrint({ printerName: name, isIp, ip, port, pageFormat: format });
      } else if (electron && electron.ipcRenderer) {
        result = await electron.ipcRenderer.invoke('test-print', { printerName: name, isIp, ip, port, pageFormat: format });
      } else if (window.require) {
        result = await window.require('electron').ipcRenderer.invoke('test-print', { printerName: name, isIp, ip, port, pageFormat: format });
      }
      return result || { success: true, message: 'Test print sent!' };
    } catch (err) {
      throw new Error(err.message || 'Electron test print failed');
    }
  }

  // Fallback to WebSocket agent
  const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
  return new Promise((resolve, reject) => {
    try {
      const ws = new WebSocket(wsUrl);
      const timer = setTimeout(() => {
        ws.close();
        reject(new Error("Connection timed out"));
      }, 3000);

      ws.onopen = () => {
        clearTimeout(timer);
        const testText = "================================\n       TEST PRINT SUCCESSFUL    \n================================\n" +
          `Printer: ${name}\nTime: ${new Date().toLocaleString()}\n` +
          "================================\n\n\n";
        ws.send(JSON.stringify({
          printerName: name,
          text: testText,
          html: `<h3>RestroPro Test Print</h3><p>Printer: ${name}</p>`
        }));
      };

      ws.onmessage = (event) => {
        try {
          const res = JSON.parse(event.data);
          if (res.success) {
            resolve({ success: true, message: res.message || 'Test print sent' });
          } else {
            reject(new Error(res.message || 'Test print failed'));
          }
        } catch (e) {
          reject(e);
        } finally {
          ws.close();
        }
      };

      ws.onerror = (err) => {
        clearTimeout(timer);
        reject(new Error("WebSocket printer connection failed"));
      };
    } catch (e) {
      reject(e);
    }
  });
};

// Generate Payment Voucher Thermal HTML
export const generatePaymentVoucherHTML = (details, pageFormat = '58') => {
  const {
    voucherNo = '',
    title = 'PAYMENT RECEIPT',
    date = new Date(),
    partyName = 'Customer',
    partyPhone = '',
    partyPan = '',
    partyType = 'Customer',
    invoicesSettled = [],
    paymentMethods = [],
    totalAmount = 0,
    remainingDue = 0,
    notes = '',
    storeSettings = {},
    currency = 'Rs.',
    cashier = ''
  } = details || {};

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const paperWidthCss = is58mm ? '48mm' : '72mm';
  const titleSize = is58mm ? '1.05rem' : '1.3rem';
  const textSize = is58mm ? '0.78rem' : '0.88rem';

  const storeName = storeSettings?.store_name || 'RESTAURANT';
  const address = storeSettings?.address || '';
  const phone = storeSettings?.phone || '';
  const panNo = storeSettings?.pan_no || storeSettings?.panNo || '';
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');
  const fmt = (v) => `${curSymbol} ${Number(v || 0).toFixed(2)}`;

  const formattedDate = date ? new Date(date).toLocaleString() : new Date().toLocaleString();

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          @page { size: auto; margin: 2mm 3mm !important; }
          * { box-sizing: border-box; }
          html, body {
            font-family: 'Courier New', Courier, monospace;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            color: #000;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt-wrapper {
            width: 100% !important;
            max-width: ${paperWidthCss} !important;
            margin: 0 auto !important;
            padding: ${is58mm ? '2mm 3mm' : '3mm 4mm'} !important;
          }
          .center { text-align: center; }
          .bold { font-weight: 900; }
          .store-name { font-size: ${titleSize}; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; }
          .sub-text { font-size: ${textSize}; line-height: 1.2; }
          .divider { border-bottom: 1px dashed #000; margin: 4px 0; }
          .double-divider { border-bottom: 2px solid #000; margin: 5px 0; }
          .banner { border: 2px solid #000; padding: 3px 4px; margin: 5px 0; font-weight: 900; text-align: center; font-size: 0.95rem; }
          .row { display: flex; justify-content: space-between; font-size: ${textSize}; padding: 1.5px 0; }
          .highlight-row { display: flex; justify-content: space-between; font-size: 1rem; font-weight: 900; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; padding: 3px 0; margin: 4px 0; }
          .due-row { display: flex; justify-content: space-between; font-size: 0.95rem; font-weight: 900; padding: 2px 0; color: #000; }
          .cutter-space { height: 8mm; clear: both; }
        </style>
      </head>
      <body>
        <div class="receipt-wrapper">
          <div class="center">
            <div class="store-name">${storeName}</div>
            ${address ? `<div class="sub-text">${address}</div>` : ''}
            ${phone ? `<div class="sub-text">Tel: ${phone}</div>` : ''}
            ${panNo ? `<div class="sub-text bold">PAN / VAT: ${panNo}</div>` : ''}
          </div>

          <div class="banner">${title.toUpperCase()}</div>

          <div class="row">
            <span>Voucher No:</span>
            <span class="bold">${voucherNo || ('VCH-' + Date.now().toString().slice(-6))}</span>
          </div>
          <div class="row">
            <span>Date & Time:</span>
            <span>${formattedDate}</span>
          </div>
          <div class="row">
            <span>${partyType}:</span>
            <span class="bold">${partyName}</span>
          </div>
          ${partyPhone ? `<div class="row"><span>Phone:</span><span>${partyPhone}</span></div>` : ''}
          ${partyPan ? `<div class="row"><span>PAN:</span><span>${partyPan}</span></div>` : ''}

          <div class="divider"></div>

          ${invoicesSettled && invoicesSettled.length > 0 ? `
            <div style="font-size: ${textSize}; font-weight: bold; margin-bottom: 2px;">${partyType === 'Supplier' ? 'SETTLED BILLS:' : 'SETTLED INVOICES:'}</div>
            ${invoicesSettled.map(inv => `
              <div class="row" style="font-size: 0.75rem;">
                <span>#${inv.invoice_no || inv.purchase_invoice_no || inv.invoice_id || inv.purchase_id || inv.ref_no || '-'}</span>
                <span>${inv.original_due !== undefined ? `Due: ${fmt(inv.original_due)} | ` : ''}Cleared: <b>${fmt(inv.allocated || inv.amount_applied || inv.amount)}</b></span>
              </div>
            `).join('')}
            <div class="divider"></div>
          ` : ''}

          <div style="font-size: ${textSize}; font-weight: bold; margin-bottom: 2px;">PAYMENT DETAILS:</div>
          ${paymentMethods && paymentMethods.length > 0 ? paymentMethods.map(pm => `
            <div class="row">
              <span>${pm.method || pm.title || 'Payment'}:</span>
              <span class="bold">${fmt(pm.amount)}</span>
            </div>
          `).join('') : `
            <div class="row">
              <span>Payment Mode:</span>
              <span class="bold">${details.paymentMethod || 'Cash'}</span>
            </div>
          `}

          <div class="highlight-row">
            <span>${partyType === 'Supplier' ? 'TOTAL PAID:' : 'TOTAL RECEIVED:'}</span>
            <span>${fmt(totalAmount)}</span>
          </div>

          <div class="due-row">
            <span>REMAINING DUE:</span>
            <span>${fmt(remainingDue)}</span>
          </div>

          ${notes ? `
            <div class="divider"></div>
            <div class="sub-text" style="font-style: italic;">Ref/Notes: ${notes}</div>
          ` : ''}

          <div class="double-divider"></div>
          <div class="row" style="margin-top: 10px; font-size: 0.75rem;">
            <span>${partyType === 'Supplier' ? 'Paid By:' : 'Received By:'} ${cashier || 'Cashier'}</span>
            <span>${partyType === 'Supplier' ? 'Supplier Sign: ________' : 'Customer Sign: ________'}</span>
          </div>

          <div class="center sub-text" style="margin-top: 8px;">*** Thank you! ***</div>
          <div class="cutter-space"></div>
        </div>
      </body>
    </html>
  `;
};

// Generate Payment Voucher Plain Text (ESC/POS)
export const generatePaymentVoucherPlainText = (details) => {
  const {
    voucherNo = '',
    title = 'PAYMENT RECEIPT',
    date = new Date(),
    partyName = 'Customer',
    partyPhone = '',
    partyType = 'Customer',
    invoicesSettled = [],
    paymentMethods = [],
    totalAmount = 0,
    remainingDue = 0,
    storeSettings = {},
    currency = 'Rs.',
    cashier = ''
  } = details || {};

  const storeName = storeSettings?.store_name || 'RESTAURANT';
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');
  const fmt = (v) => `${curSymbol} ${Number(v || 0).toFixed(2)}`;

  let txt = "================================\n";
  txt += `<center><b><big>${storeName}</big></b></center>\n`;
  if (storeSettings?.address) txt += `<center>${storeSettings.address}</center>\n`;
  if (storeSettings?.pan_no) txt += `<center>PAN: ${storeSettings.pan_no}</center>\n`;
  txt += "--------------------------------\n";
  txt += `<center><b>${title.toUpperCase()}</b></center>\n`;
  txt += `Voucher: ${voucherNo || '-'}\n`;
  txt += `Date: ${new Date(date).toLocaleString()}\n`;
  txt += `${partyType}: ${partyName} ${partyPhone ? `(${partyPhone})` : ''}\n`;
  txt += "--------------------------------\n";

  if (invoicesSettled && invoicesSettled.length > 0) {
    txt += `${partyType === 'Supplier' ? 'SETTLED BILLS:' : 'SETTLED INVOICES:'}\n`;
    invoicesSettled.forEach(inv => {
      txt += `#${inv.invoice_no || inv.purchase_invoice_no || inv.invoice_id || inv.purchase_id || inv.ref_no}: Cleared ${fmt(inv.allocated || inv.amount)}\n`;
    });
    txt += "--------------------------------\n";
  }

  if (paymentMethods && paymentMethods.length > 0) {
    paymentMethods.forEach(pm => {
      txt += `${pm.method || pm.title}: ${fmt(pm.amount)}\n`;
    });
  } else {
    txt += `Mode: ${details.paymentMethod || 'Cash'}\n`;
  }

  txt += "================================\n";
  txt += `<b>${partyType === 'Supplier' ? 'TOTAL PAID:     ' : 'TOTAL RECEIVED: '} ${fmt(totalAmount)}</b>\n`;
  txt += `<b>REMAINING DUE:  ${fmt(remainingDue)}</b>\n`;
  txt += "================================\n";
  txt += `${partyType === 'Supplier' ? 'Paid By:' : 'Received By:'} ${cashier || 'Cashier'}\n`;
  txt += "<center>*** Thank you! ***</center>\n\n\n";
  return txt;
};

// Universal Payment Voucher Print function
export const printPaymentVoucher = async (details) => {
  const isElectron = isElectronEnv();
  const electron = getElectron();
  const isBluetooth = !!connectedBluetoothDevice;
  const billPrinter = localStorage.getItem("billPrinter") || "Default";
  const pageFormat = details.pageFormat || localStorage.getItem('page_format') || '58';

  const html = generatePaymentVoucherHTML(details, pageFormat);
  const plainText = generatePaymentVoucherPlainText(details);

  if (isBluetooth) {
    try {
      await printViaWebBluetooth(html, plainText);
      return;
    } catch (btErr) {
      console.warn("Bluetooth voucher print failed:", btErr);
    }
  }

  if (isElectron) {
    try {
      if (electron && electron.ipcRenderer) {
        electron.ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html,
          plainText,
          copies: 1,
          pageFormat
        });
        return;
      } else if (window.require) {
        window.require('electron').ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html,
          plainText,
          copies: 1,
          pageFormat
        });
        return;
      }
    } catch (elErr) {
      console.warn("Electron silent voucher print failed, falling back to agent:", elErr);
    }
  }

  // Web Browser fallback
  try {
    await printViaWebSocketAgent(billPrinter, html, plainText);
  } catch (wsErr) {
    console.warn("WebSocket agent unavailable, using iframe print for voucher:", wsErr);
    const iframe = document.getElementById('voucher-print-iframe') || document.createElement('iframe');
    iframe.id = 'voucher-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.width = '300px';
    iframe.style.height = '600px';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      iframe.contentWindow.print();
    }, 500);
  }
};


